import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { AlertsService } from 'src/app/demo/service/alerts.service';
import { ChangeSingleSelect, Field, Rules } from 'src/app/demo/interfaces/form-print.interface';
import { fieldsMatchValidator } from './custom-validators';
import { FileSelectEvent } from 'primeng/fileupload';
import { InputNumberInputEvent } from 'primeng/inputnumber';

@Component({
	selector: 'app-form-print',
	templateUrl: './form-print.component.html',
	styleUrl: './form-print.component.scss'
})
export class FormPrintComponent implements OnInit, OnChanges {

	@Input() textButtonSubmit: string = 'Guardar';
	@Input() formFilter: boolean = false;
	@Input() hideAlertToSubmit: boolean = false;
	@Input() fieldsToCompare: { controlName: string, matchingControlName: string }[] = [];
	@Input() showButtonsSubmit: boolean = true;
	@Input() updatedValues: { key: string, value: any, disabled?: boolean, rules?: Rules | null }[] = [];
	@Input() showClearFilters: boolean = false;
	@Output() sendQuestions: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
	@Output() cancelQuestions: EventEmitter<void> = new EventEmitter<void>();
	@Output() onChangeSingleSelect: EventEmitter<ChangeSingleSelect> = new EventEmitter<ChangeSingleSelect>();
	@Output() onChangeValueField: EventEmitter<{ key: string, value: string | number }> = new EventEmitter<{ key: string, value: string | number }>();
	@Output() onChangeValueDateField: EventEmitter<{ key: string, value: Date, selectedField: number }> = new EventEmitter<{ key: string, value: Date, selectedField: number }>();
	@Output() validFormPrint: EventEmitter<boolean> = new EventEmitter<boolean>(false);

	_questions: Field[] = [];

	@Input()
	set questions(value: Field[]) {
		// Evita que los valores [], undefined, o null, cambie el los valores de los campos
		if (!value || value.length === 0) return;

		this._questions = value;

		// Si el formulario ya existe, solo actualizamos los campos
		if (this.form) {
			value.forEach((question) => {
				const control = this.form.get(question.key);

				if (control) {
					if (control.disabled !== question.disabled) {
						question.disabled ? control.disable() : control.enable();
					}

					const newValue = question.typeInput === 'date' ? this.normalizeDate(question.value) : question.value;

					if (control.value !== newValue) {
						control.setValue(newValue, { emitEvent: false });
					}

					const validators = this.generateValidators(question.rules);
					control.setValidators(validators);
					control.updateValueAndValidity({ emitEvent: false });
				} else {
					const validators = this.generateValidators(question.rules);
					const initialValue = question.typeInput === 'date' ? this.normalizeDate(question.value) : question.value;

					this.form.addControl(
						question.key,
						new FormControl({ value: initialValue, disabled: question.disabled }, validators)
					);
				}
			});

			this.emitValidationStatus();

		} else {
			// Primera vez: se construye el formulario completo
			const objectForm = {};

			value.forEach((question) => {
				const validators = this.generateValidators(question.rules);
				const initialValue = question.typeInput === 'date' ? this.normalizeDate(question.value) : question.value;

				objectForm[question.key] = new FormControl(
					{ value: initialValue, disabled: question.disabled },
					validators
				);
			});

			this.form = new FormGroup(objectForm);
		}
	}


	get questions(): Field[] {
		return this._questions;
	}

	private normalizeDate(value: any): Date | null {
		if (!value) return null;
		return value instanceof Date ? value : new Date(value);
	}

	form: FormGroup = null;

	constructor(
		private alertService: AlertsService,
	) { }

	ngOnInit(): void {
		const objectForm = {};
		this.questions.forEach((item: any) => {
			if (item.typeInput === 'date-range') {
				objectForm[item.range[0].key] = new FormControl(item.range[0].value, this.generateValidators(item.range[0].rules));
				objectForm[item.range[1].key] = new FormControl(item.range[1].value, this.generateValidators(item.range[1].rules));
			} else {
				const validators = this.generateValidators(item.rules);
				objectForm[item.key] = new FormControl({ value: item.value, disabled: item.disabled }, validators);
			}
		});
		this.form = new FormGroup(objectForm);
		const formValidators = [];
		this.fieldsToCompare.forEach(pair => {
			formValidators.push(fieldsMatchValidator(pair.controlName, pair.matchingControlName));
		});
		this.form = new FormGroup(objectForm, { validators: formValidators.length > 0 ? formValidators : null });

		this.listenToFormChanges();
		this.emitValidationStatus();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['updatedValues'] && changes['updatedValues'].currentValue) {
			this.updatedValues.forEach(updated => {
				const fieldControl = this.form.get(updated.key);
				if (fieldControl) {
					fieldControl.setValue(updated.value);
					const validators = this.generateValidators(updated.rules);
					fieldControl.setValidators(validators)
					if (updated.disabled) fieldControl.disable();
					else fieldControl.enable();
				}
			});
		}
	}

	/**
	  * @author Ministerio de cultura
	  * @createdate 2024-10-14
	  * @description Método que genera un conjunto de validadores basados en las reglas proporcionadas.
	  * @param {Record<string, any>} rules - Objeto JSON con las configuraciones de validación.
	  * @returns {ValidatorFn[]} Arreglo de validadores para aplicar al campo del formulario.
	  */
	generateValidators(rules: Record<string, any>): ValidatorFn[] {
		if (!rules) return [];
		const validators: ValidatorFn[] = [];
		for (const key in rules) {
			if (Object.prototype.hasOwnProperty.call(rules, key)) {
				switch (key) {
					case 'required':
						if (rules[key] === true) {
							validators.push(Validators.required);
						}
						break;
					case 'minLength':
						validators.push(Validators.minLength(rules[key]));
						break;
					case 'maxLength':
						validators.push(Validators.maxLength(rules[key]));
						break;
					case 'email':
						// Mismo caso que `required` arriba: es un flag booleano.
						if (rules[key] === true) {
							validators.push(Validators.email);
						}
						break;
					case 'min':
						validators.push(Validators.min(rules[key]));
						break;
					case 'max':
						validators.push(Validators.max(rules[key]));
						break;
				}
			}
		}
		return validators;
	}

	/**
	  * @author Ministerio de cultura
	  * @createdate 2024-10-14
	  * @description Metodo que envia las respuestas del formulario al componente padre.  
	  */
	submitForm(): void {
		if (this.form.valid) {
			if (!this.formFilter) {
				if (!this.hideAlertToSubmit) {
					this.alertService.showConfirmAlert({ html: '¿Desea enviar la información registrada?' }).then(isConfirm => {
						if (isConfirm.isConfirmed) this.sendQuestions.emit(this.form);
					});
				} else {
					this.sendQuestions.emit(this.form);
				}
			} else {
				this.sendQuestions.emit(this.form);
			}
		} else this.form.markAllAsTouched();
	}

	/**
	  * @author Ministerio de cultura
	  * @createdate 2024-12-06
	  * @description Metodo que carga el archivo seleccionado al valor del formulario. 
	  * @param $event Evento emitido por el campo file.
	  * @param key Nombre del campo.
	  */
	onSelectFileToByField($event: FileSelectEvent, key: string): void {
		const field = this.form.get(key);
		if (field) field.setValue($event.currentFiles[0]);
	}

	/**
	  * @author Ministerio de cultura
	  * @createdate 2024-12-06
	  * @description Metodo que emite el valor sobre los campos numericos. 
	  * @param $event Evento emitido por el campo tipo numerico.
	  */
	onChangeValueInput($event: InputNumberInputEvent, key: string): void {
		this.onChangeValueField.emit({ key, value: $event.value })
	}

	/**
	  * @author Ministerio de cultura
	  * @createdate 2024-12-11
	  * @description Metodo que limpia los campos del formulario y emite el submit del formulario. 
	  */
	onClickClearFiltersForm(): void {
		this.form.reset();
		this.sendQuestions.emit(this.form);
	}

	/**
	  * @author Ministerio de Cultura
	  * @createdate 2025-04-01
	  * @description Verifica si un campo del formulario es inválido.
	  * El campo es considerado inválido si tiene errores de validación y ha sido modificado (dirty) o tocado (touched).
	  *
	  * @param {string} questionKey - Nombre del campo a validar dentro del formulario `compromisoForm`.
	  * @returns {boolean} `true` si el campo es inválido, `false` en caso contrario.
	  */
	isInvalid(questionKey: string): boolean {
		const control = this.form.get(questionKey);
		return control && control.invalid && (control.dirty || control.touched);
	}

	/**
	  * @author Ministerio de cultura
	  * @createdate 2025-04-02
	  * @description Se suscribe a los cambios en los campos del formulario y actualiza el estado de validación.
	  * Cada vez que un usuario modifique un campo, se verificará si el formulario es válido o no.
	  */
	private listenToFormChanges(): void {
		this.form.valueChanges.subscribe(() => {
			this.emitValidationStatus();
		});
	}

	/**
	  * @author Ministerio de cultura
	  * @createdate 2025-04-02
	  * @description Emite el estado de validez del formulario. 
	  *              Si todos los campos están deshabilitados, emite `true`. 
	  *              De lo contrario, emite el estado de validez del formulario.
	  */
	private emitValidationStatus(): void {
		const isFormFullyDisabled = this._questions.every(item => item.disabled === true);
		this.validFormPrint.emit(isFormFullyDisabled ? true : this.form.valid);
	}

	handleInputNumber(event: InputNumberInputEvent, question: any): void {

		const control = this.form.get(question.key);

		if (question.rules?.maxLength != null) {
			const value = String(event.value || '')
			const truncated = Number(value.slice(0, question.rules.maxLength));
			if (control.value !== truncated)
				control.setValue(truncated === 0 ? undefined : truncated);
		}

		if (question.rules?.max != null) {
			const value = Number(event.value)
			control.setValue(value > 100 ? question.rules.max : value);
		}

		this.onChangeValueInput(event, question.key);
	}

	handleOverlayClose(question: Field, index: number) {
		const value = this.form.get(question.key)?.value;
		this.onChangeSingleSelect.emit({ value: value, key: question.key, selectedField: index});
	}

	isMaxLengthReached(question: Field): boolean {
		const value = String(this.form.get(question.key)?.value || '');
		return question.rules?.maxLength != null && value.length >= question.rules.maxLength;
	}

}